import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ShoppingCart,
  Heart,
  ScrollText,
  Home,
  Sparkles,
  Gamepad2,
  Volume2,
  VolumeX,
  Award,
} from "lucide-react";
import { useStore } from "../context/StoreContext";
import { bracketFor } from "../data/leaderboard";
import Coin from "./Coin";
import NotificationCenter from "./NotificationCenter";
import sfx from "../utils/sound";

const Header = () => {
  const { name, coins, cart, wishlist, soundOn, setSoundOn, canClaimDaily, stickers, gameStats, gamesWon, avatar } = useStore();
  const nav = useNavigate();
  const cartCount = cart.reduce((sum, c) => sum + c.qty, 0);
  const totalWins = Object.values(gamesWon || {}).reduce((s, n) => s + n, 0);
  const playerScore = (gameStats?.coinsEarned || 0) + totalWins * 15;
  const rank = bracketFor(playerScore);

  // Collapse the header to just logo + wallet after 60px of scroll on every
  // viewport so kids get more screen real-estate as soon as they start
  // browsing. Uses hysteresis (collapse >60, expand <20) so the collapse
  // never oscillates when the browser's scroll anchoring compensates for
  // the shrinking header height. Scroll handler is rAF-throttled.
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    let ticking = false;
    let latest = false;
    const evaluate = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      // Hysteresis: don't re-expand until well below the collapse threshold.
      const next = latest ? y > 20 : y > 60;
      if (next !== latest) {
        latest = next;
        setCollapsed(next);
      }
      ticking = false;
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(evaluate);
    };
    // Disable scroll anchoring on <body> so the header shrinking does not
    // pull scrollY back and cause the collapse/expand ping-pong.
    const prevAnchor = document.body.style.overflowAnchor;
    document.body.style.overflowAnchor = "none";
    evaluate();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      document.body.style.overflowAnchor = prevAnchor;
    };
  }, []);

  return (
    <header
      data-testid="app-header"
      data-collapsed={collapsed ? "1" : "0"}
      className="sticky top-0 z-40 border-b-4"
      style={{
        background: "#FFF9E6",
        borderColor: "#2b2b2b",
        transition: "box-shadow 200ms ease",
        boxShadow: collapsed ? "0 4px 0 rgba(0,0,0,0.08)" : "none",
      }}
    >
      <div className="mx-auto max-w-6xl px-3 sm:px-6 py-3 flex items-center justify-between gap-2 sm:gap-3">
        <Link
          to="/shop"
          data-testid="header-logo"
          className="flex items-center gap-1 sm:gap-2 shrink min-w-0"
          onClick={() => sfx.click()}
        >
          <span className="wiggle text-2xl sm:text-4xl leading-none" aria-hidden="true">☕</span>
          <span
            className="font-display text-base sm:text-2xl leading-none truncate"
            style={{ color: "#FF5A5F" }}
          >
            67<span style={{ color: "#00A699" }}>Cafe</span>
          </span>
        </Link>

        <div className="flex items-center gap-1 sm:gap-3 shrink-0">
          <div
            data-testid="wallet-balance"
            className="coin-pill text-xs sm:text-base"
            title={`${name || "Shopper"}'s coins`}
          >
            <Coin size={20} />
            <span data-testid="wallet-coins-value">{coins}</span>
          </div>

          <button
            onClick={() => {
              setSoundOn(!soundOn);
              if (!soundOn) sfx.click();
            }}
            aria-label={soundOn ? "Mute sounds" : "Enable sounds"}
            data-testid="sound-toggle"
            className={`rounded-full items-center justify-center shrink-0 ${collapsed ? "hidden" : "flex"}`}
            style={{
              width: 36,
              height: 36,
              background: soundOn ? "#54A0FF" : "#fff",
              color: soundOn ? "#fff" : "#484848",
              border: "3px solid #2b2b2b",
              boxShadow: "0 3px 0 #2b2b2b",
            }}
          >
            {soundOn ? (
              <Volume2 size={18} strokeWidth={3} />
            ) : (
              <VolumeX size={18} strokeWidth={3} />
            )}
          </button>

          {!collapsed && <NotificationCenter />}

          <Link
            to="/profile"
            data-testid="header-avatar-btn"
            aria-label="Open my profile"
            onClick={() => sfx.click()}
            className={`rounded-full items-center justify-center shrink-0 ${collapsed ? "hidden" : "flex"}`}
            style={{
              width: 36,
              height: 36,
              background: "#FF5A5F",
              border: "3px solid #2b2b2b",
              boxShadow: "0 3px 0 #2b2b2b",
              textDecoration: "none",
              fontSize: 18,
              lineHeight: 1,
            }}
          >
            <span aria-hidden="true">{avatar || "🐻"}</span>
          </Link>

          <nav className={`items-center gap-2 ${collapsed ? "hidden" : "hidden lg:flex"}`}>
            <Link
              to="/shop"
              className="btn-3d btn-sm btn-ghost"
              data-testid="nav-home"
              onClick={() => sfx.click()}
            >
              <Home size={18} strokeWidth={3} /> Home
            </Link>
            <Link
              to="/games"
              className="btn-3d btn-sm btn-purple relative"
              data-testid="nav-games"
              onClick={() => sfx.click()}
            >
              <Gamepad2 size={18} strokeWidth={3} />
              Games
              {canClaimDaily && (
                <span
                  className="absolute -top-2 -right-2 rounded-full bg-white text-black text-xs font-bold flex items-center justify-center wiggle"
                  style={{ width: 22, height: 22, border: "2px solid #2b2b2b" }}
                  data-testid="daily-gift-badge"
                >
                  🎁
                </span>
              )}
            </Link>
            <Link
              to="/wishlist"
              className="btn-3d btn-sm btn-pink"
              data-testid="nav-wishlist"
              onClick={() => sfx.click()}
            >
              <Heart size={18} strokeWidth={3} fill="white" />
              {wishlist.length > 0 && <span>{wishlist.length}</span>}
            </Link>
            <Link
              to="/orders"
              className="btn-3d btn-sm btn-accent"
              data-testid="nav-orders"
              onClick={() => sfx.click()}
            >
              <ScrollText size={18} strokeWidth={3} />
              Orders
            </Link>
            <Link
              to="/stickers"
              className="btn-3d btn-sm"
              data-testid="nav-stickers"
              onClick={() => sfx.click()}
              style={{ background: "#FFB400" }}
            >
              <Award size={18} strokeWidth={3} />
              Stickers
              {stickers.length > 0 && (
                <span
                  className="ml-1 inline-flex items-center justify-center rounded-full bg-white text-black font-bold"
                  style={{ minWidth: 22, height: 22, border: "2px solid #2b2b2b" }}
                  data-testid="stickers-count-header"
                >
                  {stickers.length}
                </span>
              )}
            </Link>
            <button
              onClick={() => {
                sfx.click();
                nav("/cart");
              }}
              className="btn-3d btn-sm"
              data-testid="nav-cart"
            >
              <ShoppingCart size={18} strokeWidth={3} />
              Cart
              {cartCount > 0 && (
                <span
                  className="ml-1 inline-flex items-center justify-center rounded-full bg-white text-black font-bold"
                  style={{ minWidth: 22, height: 22, border: "2px solid #2b2b2b" }}
                  data-testid="cart-count-header"
                >
                  {cartCount}
                </span>
              )}
            </button>
          </nav>
        </div>
      </div>

      {name && !collapsed && (
        <Link
          to="/profile"
          className="mx-auto max-w-6xl px-4 sm:px-6 pb-2 -mt-1 flex items-center gap-2 text-sm flex-wrap"
          data-testid="header-greeting"
          onClick={() => sfx.click()}
          style={{ textDecoration: "none" }}
        >
          <Sparkles size={14} strokeWidth={3} style={{ color: "#FFB400" }} />
          <span style={{ color: "#484848" }}>
            Hey <strong style={{ color: "#F368E0" }}>{name}</strong> — play, shop &amp; win at 67Cafe
          </span>
          <span
            className="text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1"
            style={{
              background: rank.color,
              color: "#fff",
              border: "2px solid #2b2b2b",
              lineHeight: 1.2,
              fontFamily: "Fredoka, sans-serif",
            }}
            data-testid="header-rank-pill"
          >
            <span aria-hidden="true">{rank.emoji}</span>
            {rank.name}
          </span>
          <span
            className="text-xs px-2 py-0.5 rounded-full"
            style={{
              background: "#F368E0",
              color: "#fff",
              border: "2px solid #2b2b2b",
              lineHeight: 1.2,
            }}
          >
            View profile →
          </span>
        </Link>
      )}
    </header>
  );
};

export default Header;
